import React, {useState} from "react";
import {Box, Flex, Text, Badge, Stack, Button, useToast} from "@chakra-ui/react";
import {messageFor} from "../../api/dependents";
import {fetchDependentOwnForms, fetchDependentOwnForm, editorFormOf} from "../../api/ownForms";
import {useDependentSection, SECTION_STATES} from "./useDependentSection";
import DependentShell, {EmptyState, ScopeNote} from "./DependentShell";
import CompleteForm from "../Froms/CompleteForm";
import {formatDate} from "./format";

// Forms a guardian may see, and — at `forms: submit` — fill in for a dependent.
//
// Reading a form and signing one are different acts, and the grades say so:
// `forms: view` opens the form read-only, `forms: full` and `partial` carry
// `submit`. The clinic can let a grandparent read the paperwork without letting
// them execute it, so the button below is offered on `submit` and the form is
// opened on `view`.
//
// The asymmetry in the server's `own` scope shows through here and is worth
// stating: an outstanding form is visible to any guardian who may act on forms,
// because somebody has to fill it in; a completed one is visible only to the
// guardian who submitted it. Hiding the other parent's answers is the
// requirement — hiding the task itself would just leave it undone.
//
// The editor is the SAME one the patient's own forms open in. It submits
// through `/submit-form`, which derives the guardian from the verified session
// and the guardianship in force — never from anything this screen sends.

// The section hook reads a 404 as a revoked link from `error.response.status`;
// the operations client throws an ApiError that carries `status`.
const loadForms = async (state, dependentPatientId) => {
  try {
    return await fetchDependentOwnForms({jwt: state?.jwt}, dependentPatientId);
  } catch (error) {
    if (error?.status && !error.response) error.response = {status: error.status};
    throw error;
  }
};

export default function DependentForms({state}) {
  const section = useDependentSection({state, section: "forms", load: loadForms});
  const items = (section.data?.forms || []).map((form) => ({
    ...form,
    completed: form.status === "completed",
  }));
  const outstanding = items.filter((form) => !form.completed);
  const completed = items.filter((form) => form.completed);
  const toast = useToast();

  const [opening, setOpening] = useState(null);
  const [open, setOpen] = useState(null);

  const maySubmit = !!section.dependent?.capabilities?.forms?.includes("submit");

  const openForm = async (form) => {
    setOpening(form.formSubmissionId);
    try {
      const data = await fetchDependentOwnForm(
        {jwt: state?.jwt},
        section.dependentPatientId,
        form.formSubmissionId
      );
      setOpen({form: editorFormOf(data), patient: data?.patient});
    } catch (error) {
      toast({
        status: "error",
        title: "That form could not be opened",
        description: messageFor(error, "It may no longer be available to you."),
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setOpening(null);
    }
  };

  return (
    <DependentShell
      title="Forms"
      subtitle={`${section.displayName}'s forms`}
      displayName={section.displayName}
      accessLevel={section.dependent?.accessLevel}
      status={section.status}
      error={section.error}
      onRetry={section.reload}
      onLeave={section.leave}
    >
      {section.status === SECTION_STATES.READY &&
        (items.length === 0 ? (
          <EmptyState heading="No forms" body="There is nothing to fill in and nothing on file that you can see." />
        ) : (
          <>
            {outstanding.length > 0 && (
              <Group
                heading="Waiting to be completed"
                forms={outstanding}
                actionLabel={maySubmit ? "Fill in" : "Read"}
                opening={opening}
                onOpen={openForm}
              />
            )}
            {completed.length > 0 && (
              <Group
                heading="Completed"
                forms={completed}
                actionLabel="View"
                opening={opening}
                onOpen={openForm}
              />
            )}
            <ScopeNote scope={section.data?.recordScope} noun="forms" />
            {!maySubmit && outstanding.length > 0 && (
              <Text fontSize="xs" color="gray.500" mt={3}>
                You can read these forms. Your clinic has not given you permission to complete them
                on {section.displayName}'s behalf.
              </Text>
            )}
          </>
        ))}

      {open && (
        <CompleteForm
          form={open.form}
          patient={open.patient}
          state={state}
          close={() => {
            setOpen(null);
            section.reload();
          }}
        />
      )}
    </DependentShell>
  );
}

function Group({heading, forms, actionLabel, opening, onOpen}) {
  return (
    <Box mb={6}>
      <Text
        fontSize="11px"
        letterSpacing="0.04em"
        textTransform="uppercase"
        color="gray.500"
        mb={2}
      >
        {heading}
      </Text>
      <Stack spacing={3}>
        {forms.map((form) => (
          <Flex
            key={form.formSubmissionId}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="10px"
            p={4}
            bg="white"
            justify="space-between"
            align={{base: "flex-start", md: "center"}}
            direction={{base: "column", md: "row"}}
            gap={3}
          >
            <Box minW={0}>
              <Text fontWeight="600" color="#1a1f36">
                {form.title || "Form"}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {form.completed
                  ? `Completed ${formatDate(form.completedAt)}`
                  : `Assigned ${formatDate(form.assignedAt)}`}
              </Text>
            </Box>
            <Flex gap={2} flexShrink={0} align="center">
              {form.submittedByYou && (
                <Badge colorScheme="purple" fontSize="10px" textTransform="none" borderRadius="full" px={2}>
                  Submitted by you
                </Badge>
              )}
              <Badge
                colorScheme={form.completed ? "green" : "orange"}
                fontSize="10px"
                textTransform="none"
                borderRadius="full"
                px={2}
              >
                {form.completed ? "Completed" : "Outstanding"}
              </Badge>
              <Button
                size="xs"
                variant="outline"
                isLoading={opening === form.formSubmissionId}
                onClick={() => onOpen(form)}
              >
                {actionLabel}
              </Button>
            </Flex>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}
